import { CarFleetViewRecord } from '@navio/nsdk';
import { keepPreviousData } from '@tanstack/react-query';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiIcon } from 'noddi-ui-common';
import { ApiErrorMessage, ErrorCodes, ErrorMessage, NoddiBadge, NoddiSelectableBox, NoddiSpinner } from 'noddi-ui-web';
import {
  CountryCode,
  formatLicensePlate,
  getCarDisplayName,
  isCompleteLicensePlateNumber,
  isValidLicensePlateNumber,
  useDebounceValue
} from 'noddi-util';
import { useState } from 'react';
import { LicensePlateInput } from '../CarRegistration/LicensePlateInput';
import { FleetSwapRegistryCard } from './FleetSwapRegistryCard';
import { FleetSwapTranslations } from './fleetSwapTranslations';
import { FleetSwapCandidate, FleetSwapOutgoing, FleetSwapScope } from './fleetSwapTypes';

const PAGE_SIZE = 8;

type Props = {
  scope: FleetSwapScope;
  outgoing: FleetSwapOutgoing;
  selected: FleetSwapCandidate | null;
  onSelect: (candidate: FleetSwapCandidate | null) => void;
  translations: FleetSwapTranslations;
  errorCodes?: ErrorCodes;
};

export const FleetSwapPicker = ({ scope, outgoing, selected, onSelect, translations: t, errorCodes }: Props) => {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounceValue({ value: query, delay: 300 });

  const {
    data: carsPage,
    isPending,
    error
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedFleetViewCars,
    input: {
      userGroupIds: [scope.userGroupId],
      ...(scope.kind === 'partner'
        ? { serviceDepartmentIds: [scope.serviceDepartmentId] }
        : { brandDomains: [scope.brandDomain] }),
      search: debouncedQuery,
      pageIndex: 0,
      pageSize: PAGE_SIZE
    },
    queryConfig: { placeholderData: keepPreviousData }
  });

  const fleetCars = carsPage?.results ?? [];
  const candidates = fleetCars.filter((car) => car.id !== outgoing.carId);
  const { scheduleNameByCarId, error: enrollmentsError } = useEnrolledScheduleNames(scope, candidates);
  // A full plate the fleet does not have can be looked up in the vehicle registry instead.
  const showRegistryLookup =
    !isPending &&
    isCompleteLicensePlateNumber({ input: debouncedQuery, countryCode: CountryCode.NORWAY }) &&
    !fleetCars.some((car) => car.licensePlate.number === debouncedQuery);

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col gap-2'>
        <span className='text-xs font-semibold text-muted-foreground'>{t.replacementCar}</span>
        <LicensePlateInput
          value={query}
          onChange={(value) => {
            setQuery(value);
            onSelect(null);
          }}
          placeholder={t.licensePlatePlaceholder}
          countryCode={CountryCode.NORWAY}
        />
        {!isValidLicensePlateNumber({ input: query, countryCode: CountryCode.NORWAY }) && (
          <ErrorMessage message={t.invalidLicensePlate} />
        )}
        <p className='text-xs text-muted-foreground'>{t.searchHint}</p>
      </div>

      {(error || enrollmentsError) && <ApiErrorMessage error={[error, enrollmentsError]} errorCodes={errorCodes} />}

      {isPending ? (
        <div className='flex justify-center py-6'>
          <NoddiSpinner />
        </div>
      ) : candidates.length > 0 ? (
        <div className='flex max-h-72 flex-col gap-2 overflow-y-auto'>
          {candidates.map((car) => {
            // A car can only be on one schedule, so an enrolled car cannot take this one over.
            const enrolledIn = scheduleNameByCarId.get(car.id);
            return (
              <NoddiSelectableBox
                key={car.id}
                size='small'
                isSelected={selected?.carId === car.id}
                setIsSelected={(next) => onSelect(next ? fleetCarToCandidate(car) : null)}
                disabled={enrolledIn !== undefined}
              >
                <div className='flex items-center gap-3'>
                  <NoddiIcon name='Car' size='small' className='shrink-0 text-muted-foreground' />
                  <div className='flex min-w-0 flex-1 flex-col'>
                    <span className='text-sm font-medium tracking-wider tabular-nums'>
                      {formatLicensePlate(car.licensePlate.number)}
                    </span>
                    <span className='truncate text-xs text-muted-foreground'>{getFleetCarDisplayName(car)}</span>
                  </div>
                  {enrolledIn === undefined ? (
                    <NoddiBadge variant='success'>{t.available}</NoddiBadge>
                  ) : (
                    <NoddiBadge variant='outline'>{t.onSchedule(enrolledIn)}</NoddiBadge>
                  )}
                </div>
              </NoddiSelectableBox>
            );
          })}
        </div>
      ) : (
        !showRegistryLookup && (
          <p className='rounded-lg border border-border p-4 text-sm text-muted-foreground'>
            {debouncedQuery === '' ? t.noOtherCars : t.noMatchingCars}
          </p>
        )
      )}

      {showRegistryLookup && (
        <FleetSwapRegistryCard
          key={debouncedQuery}
          scope={scope}
          licensePlateNumber={debouncedQuery}
          isSelected={selected?.isNewToFleet === true}
          onSelect={onSelect}
          translations={t}
          errorCodes={errorCodes}
        />
      )}
    </div>
  );
};

/**
 * Which schedule each listed car is already on. The fleet view row carries it, scoped to the
 * caller's own brand or service department; partners also see enrollments under other service
 * departments through the cross-schedule list, which customers cannot read.
 */
const useEnrolledScheduleNames = (scope: FleetSwapScope, cars: CarFleetViewRecord[]) => {
  const carIds = cars.map((car) => car.id);
  const { data: enrollmentsPage, error } = noddiAsync.useGet({
    type: URLKeys.getPaginatedFleetScheduleVehiclesAll,
    input: { carIds, pageSize: carIds.length },
    queryConfig: { enabled: scope.kind === 'partner' && carIds.length > 0, placeholderData: keepPreviousData }
  });
  const scheduleNameByCarId = new Map<number, string>();
  for (const car of cars) {
    if (car.fleetSchedule) {
      scheduleNameByCarId.set(car.id, car.fleetSchedule.fleetScheduleName);
    }
  }
  for (const enrollment of enrollmentsPage?.results ?? []) {
    scheduleNameByCarId.set(enrollment.car.id, enrollment.fleetScheduleName);
  }
  return { scheduleNameByCarId, error: scope.kind === 'partner' ? error : null };
};

const getFleetCarDisplayName = (car: CarFleetViewRecord) =>
  getCarDisplayName({ car, showLicensePlate: false, showYear: false }) || null;

const fleetCarToCandidate = (car: CarFleetViewRecord): FleetSwapCandidate => ({
  carId: car.id,
  licensePlateNumber: car.licensePlate.number,
  carDisplayName: getFleetCarDisplayName(car),
  isNewToFleet: false
});
